import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router';
import { CaretLeft, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { RootState } from '../../redux/reducers';
import { handleFetchPurchase } from '../../redux/actions/purchaseActions';
import { domain } from '../../config/enviroment';
import { language } from '../../utils/language';
import useQuery from '../../hooks/useQuery';
import Spinner from '../../components/Spinner';
import { LoadContainer } from '../ProductInfo/style';
import { Body } from '../../theme/theme';
import { fetchGuideStatus } from './trackingService';
import { buildTimelineFromHistoricos, buildTimelineFromStatus, toSentenceCase, TrackingStep } from './trackingUtils';
import {
    TrackingWrapper,
    TrackingHeader,
    TimelineContainer,
    TimelineStep,
    StepIndicator,
    StepCircle,
    StepLine,
    StepContent,
    StepLabel,
    StepDate,
    BackLinkWrapper,
    BackLink,
    SummarySection,
    SummaryRow,
    SummaryLabel,
    SummaryValue,
    SummaryValueRegular,
    ErrorContainer,
    InvalidContainer,
} from './Tracking.style';

type ViewState = 'loading' | 'invalid' | 'error' | 'success';

const Tracking = () => {
    const { idCart }: any = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const query = useQuery();

    const homeState: HomeStateType = useSelector((state: RootState) => state.home);
    const purchase: any = useSelector((state: RootState) => state.purchase);
    const lang = language[homeState.default_language];

    const [viewState, setViewState] = useState<ViewState>('loading');
    const [steps, setSteps] = useState<TrackingStep[]>([]);
    const [estimatedDay, setEstimatedDay] = useState<string>('');
    const [currentStatusKey, setCurrentStatusKey] = useState<string>('');
    const [currentStatusLabel, setCurrentStatusLabel] = useState<string>('');
    const purchaseWasLoading = useRef(false);
    const isMock = process.env.REACT_APP_TRACKING_MOCK === 'true';

    const fromPurchase = query.get('from') === 'purchase';

    // Fetch purchase data (skip in mock mode to avoid redirect on missing cart)
    useEffect(() => {
        if (isMock) return;
        dispatch(
            handleFetchPurchase(
                idCart,
                `${domain}/vende/${homeState.catalogueUrlName}`,
                window.location,
                history,
                homeState.default_language
            )
        );
    }, [idCart]);

    // Load guide status
    const loadGuideStatus = async () => {
        try {
            const url = `${domain}/vende/${homeState.catalogueUrlName}`;
            const response = await fetchGuideStatus(String(idCart), url);

            const guide = response.success && response.guide;

            if (guide) {
                const hasHistoricos = guide.historicos?.length > 0;
                const timelineSteps = hasHistoricos
                    ? buildTimelineFromHistoricos(guide.historicos)
                    : buildTimelineFromStatus(guide.status, guide.date);
                setSteps(timelineSteps);
                setEstimatedDay(guide.estimatedDeliveryDate || purchase.shipping?.aveonline?.daysDelivery || '');
                setCurrentStatusLabel(guide.status || '');

                const completedSteps = timelineSteps.filter(s => s.completed);
                if (completedSteps.length > 0) {
                    setCurrentStatusKey(completedSteps[completedSteps.length - 1].statusKeyword);
                }

                setViewState('success');
            } else {
                setViewState('error');
            }
        } catch {
            setViewState('error');
        }
    };

    // In mock mode, skip purchase validation and go straight to guide status
    useEffect(() => {
        if (!isMock) return;
        loadGuideStatus();
    }, []);

    // In real mode, wait for purchase fetch to complete before checking eligibility
    useEffect(() => {
        if (isMock) return;

        if (purchase.loading) {
            purchaseWasLoading.current = true;
            return;
        }

        // Only evaluate after fetch has completed (loading went true→false)
        if (!purchaseWasLoading.current) return;

        const isEligible =
            purchase.cartState === 'pagado' &&
            Boolean(purchase.guideId || purchase.guide?.aveonline);

        if (!isEligible) {
            setViewState('invalid');
            return;
        }

        loadGuideStatus();
    }, [purchase.loading]);

    const handleBack = () => {
        history.push(`/vende/${homeState.catalogueUrlName}/purchase/${idCart}`);
    };

    if (viewState === 'loading') {
        return (
            <Body>
                <LoadContainer>
                    <Spinner color={homeState.color} />
                </LoadContainer>
            </Body>
        );
    }

    if (viewState === 'invalid') {
        return (
            <Body>
                {fromPurchase && (
                    <BackLinkWrapper>
                        <BackLink onClick={handleBack}>
                            <CaretLeft size={16} weight="regular" />
                            {lang['backToDetail']}
                        </BackLink>
                    </BackLinkWrapper>
                )}
                <TrackingWrapper>
                    <InvalidContainer>
                        <WarningCircle size={64} color="#999" weight="regular" />
                        <p>{lang['trackingNotApplicable']}</p>
                    </InvalidContainer>
                </TrackingWrapper>
            </Body>
        );
    }

    if (viewState === 'error') {
        return (
            <Body>
                {fromPurchase && (
                    <BackLinkWrapper>
                        <BackLink onClick={handleBack}>
                            <CaretLeft size={16} weight="regular" />
                            {lang['backToDetail']}
                        </BackLink>
                    </BackLinkWrapper>
                )}
                <TrackingWrapper>
                    <ErrorContainer>
                        <WarningCircle size={64} color="#E53935" weight="regular" />
                        <p>{lang['trackingError']}</p>
                    </ErrorContainer>
                </TrackingWrapper>
            </Body>
        );
    }

    return (
        <Body>
            {fromPurchase && (
                <BackLinkWrapper>
                    <BackLink onClick={handleBack}>
                        <CaretLeft size={14} weight="bold" />
                        {lang['backToDetail']}
                    </BackLink>
                </BackLinkWrapper>
            )}

            <TrackingWrapper>
                <TrackingHeader>
                    <CheckCircle size={64} color="#4CAF50" weight="regular" />
                    <h2>{lang['trackMyOrder']}</h2>
                    <p>{lang['orderDetails']}</p>
                </TrackingHeader>

                <TimelineContainer>
                    {steps.map((step, index) => {
                        const isLast = index === steps.length - 1;
                        return (
                            <TimelineStep key={step.number}>
                                <StepIndicator>
                                    <StepCircle $completed={step.completed}>
                                        {step.number}
                                    </StepCircle>
                                    {!isLast && <StepLine $completed={step.completed} />}
                                </StepIndicator>
                                <StepContent>
                                    <StepLabel>
                                        {lang[step.labelKey]}
                                    </StepLabel>
                                    {step.completed && step.date && (
                                        <>
                                            <StepDate>{step.date}</StepDate>
                                            {step.time && <StepDate>{step.time}</StepDate>}
                                        </>
                                    )}
                                </StepContent>
                            </TimelineStep>
                        );
                    })}
                </TimelineContainer>
            </TrackingWrapper>

            <SummarySection>
                <SummaryRow>
                    <SummaryLabel>{lang['orderStatus']}</SummaryLabel>
                    <SummaryValue>
                        {toSentenceCase(currentStatusLabel || (currentStatusKey ? lang[currentStatusKey] : ''))}
                    </SummaryValue>
                </SummaryRow>
                {estimatedDay && (
                    <SummaryRow>
                        <SummaryLabel>{lang['estimatedDeliveryDate']}</SummaryLabel>
                        <SummaryValueRegular>{estimatedDay}</SummaryValueRegular>
                    </SummaryRow>
                )}
            </SummarySection>
        </Body>
    );
};

export default Tracking;
